import { useCallback, useContext, useMemo } from 'react';
import { Link, matchPath, NavLink, useLocation } from 'react-router-dom';
import {
    AppId,
    CountriesSelect,
    FCX,
    Header,
    HeaderLogo,
    ID,
    Navigation,
    ProductLogo,
    SearchBox,
    Toggle,
    usePlatformFeatureFlag
} from '@datassential/platform-ui-lib';
import cn from 'classnames';
import { CountriesEnum, GlobalContext } from '../../GlobalContext';
import RoutesEnum from '../../core/enums/RoutesEnum';
import './PlatformHeader.scss';

export const NEWS_FEATURE_FLAG = 'RPRO News';

/**
 * Report Pro platform header: product logo, the content-region selector ("US & CA" or "Global")
 * and the navigation items available for the selected region.
 */
const PlatformHeader: FCX = () => {
    const location = useLocation();

    const { setIsSearchOpened, inMyLibrary, setInMyLibrary, selectedCountryId, setSelectedCountryId } = useContext(GlobalContext);
    const isNewsEnabled = usePlatformFeatureFlag(NEWS_FEATURE_FLAG);
    const isHomepage = matchPath(RoutesEnum.Home, location.pathname);

    const navigationItems = useMemo(() => {
        const myListLink = {
            id: 'My List',
            content: (
                <NavLink to={RoutesEnum.ReadingList} className={'PlatformHeader__nav-item'}>
                    My List
                </NavLink>
            )
        };

        if (selectedCountryId === CountriesEnum.Global) {
            return [myListLink];
        }

        const result = [
            myListLink,
            ...(isNewsEnabled
                ? [
                      {
                          id: 'News',
                          content: (
                              <NavLink
                                  to={RoutesEnum.News}
                                  className={'PlatformHeader__nav-item PlatformHeader__nav-item--clickable'}
                              >
                                  News
                              </NavLink>
                          )
                      }
                  ]
                : []),
            {
                id: 'Alerts',
                content: (
                    <NavLink to={RoutesEnum.Alerts} className={'PlatformHeader__nav-item'}>
                        Alerts
                    </NavLink>
                )
            },
            {
                id: 'search',
                content: (
                    <SearchBox
                        testId={'selectDEMOHeaderReportProSearch'}
                        tooltipId="no-data"
                        onClick={() => setIsSearchOpened(true)}
                        style={{ cursor: 'pointer' }}
                        inputProps={{
                            value: '',
                            setValue: () => {},
                            placeholder: 'Search in Report Pro',
                            testId: 'inputDEMOHeaderReportProSearch',
                            style: {
                                pointerEvents: 'none'
                            }
                        }}
                        dropdownProps={{
                            id: 'no-data',
                            isOpened: false
                        }}
                    />
                )
            }
        ];

        if (isHomepage) {
            result.unshift({
                id: 'checkbox',
                content: <Toggle isChecked={inMyLibrary} onChange={() => setInMyLibrary(!inMyLibrary)} label="in My Library" />
            });
        }

        return result;
    }, [inMyLibrary, isHomepage, isNewsEnabled, setInMyLibrary, setIsSearchOpened, selectedCountryId]);

    const countriesOptions = useMemo(
        () => [
            { id: 'usa', shortName: 'US & CA', name: 'U.S. & Canada' },
            { id: 'global', shortName: 'GLO', name: 'Global' }
        ],
        []
    );

    const handleSelectCountry = useCallback(
        (v: ID) => {
            setSelectedCountryId(v as CountriesEnum);
        },
        [setSelectedCountryId]
    );

    return (
        <Header
            className={cn('PlatformHeader', { 'is-homepage': isHomepage })}
            leftContent={
                <>
                    <Link to="/ " data-testid={'buttonDEMOHeaderLogo'}>
                        <HeaderLogo>
                            <ProductLogo appId={AppId.ReportPro} />
                        </HeaderLogo>
                    </Link>
                    <CountriesSelect
                        style={{ marginLeft: 10 }}
                        options={countriesOptions}
                        value={selectedCountryId}
                        setValue={handleSelectCountry}
                        testId={'selectDEMOHeaderCountry2'}
                    />
                </>
            }
            rightContent={<Navigation data={navigationItems} />}
        />
    );
};

export default PlatformHeader;
